import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/uiComponents/components/ui/table";

import { Button } from "@/uiComponents/components/ui/button";
import { IconPlus } from "@tabler/icons-react";

import { EllipsisVertical, SquarePen, Trash2 } from "lucide-react";

import { ItemError } from "@/enums/errorMessages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";

import { ErrorHelper } from "@/utils/errorHelper";
import DeleteModal from "../DeleteModal";
import { toastMessage } from "@/utils/toastHelper";
import { deleteItemApi, getItemApi } from "@/modules/organizationServices";
import { deleteItem, setItems } from "@/store/redux/organization/itemSlice";
import { Items } from "@/types/organization";
import AddItems from "./AddItem";
import UpdateItem from "./UpdateItem";
import { AddExpenseButton } from "@/utils/AddExpenseButton";
export default function Item() {
  const dispatch = useDispatch();
  const items = useSelector((state: RootState) => state.item.items);
  const role = useSelector((state: RootState) => state.userDetail.role);
  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<Items | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const handleDeleteItem = async (id: number) => {
    try {
      await deleteItemApi(id);
      dispatch(deleteItem(id));
      toastMessage("success", ItemError.ITEM_DELETE);
      await getItems();
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const getItems = async () => {
    try {
      const res = await getItemApi();
      const response = res.items;
      if (response != undefined) {
        const items = Array.isArray(response) ? response : [response];
        dispatch(setItems(items));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  useEffect(() => {
    getItems();
  }, []);

  return (
    <div className="flex flex-1 flex-col max-w-screen md:ml-4 px-4 ">
      <div>
        <h2 className="text-2xl  font-semibold w-full">Items</h2>

        <AddItems onClose={() => setIsAddOpen(false)} isOpen={isAddOpen} />

        {selectedItem && (
          <UpdateItem
            onClose={() => setIsUpdateOpen(false)}
            isOpen={isUpdateOpen}
            item={selectedItem}
          />
        )}
      </div>
     <AddExpenseButton />

      <div className="overflow-x-auto max-w-4xl mt-6 rounded-md ">
        <Table className="min-w-full bg-white border-1 ">
          <TableRow>
            <TableHead className="text-right" colSpan={4}>
              <Button
                className="bg-[#191F29]  hover:bg-[#2A313D] active:bg-[#001533] text-white hover:text-white cursor-pointer whitespace-nowrap mt-2 mb-2  "
                onClick={() => setIsAddOpen(true)}
                variant="outline"
                size="sm"
              >
                <IconPlus />
                <span className="hidden lg:inline ml-1">Add Item</span>
              </Button>
            </TableHead>
          </TableRow>

          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.name}</TableCell>

                <TableCell colSpan={10} className="text-right pr-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-10 w-10 p-0 cursor-pointer hover:bg-gray-200 "
                      >
                        <span className="sr-only">Open menu</span>
                        <EllipsisVertical className=" text-gray-700 text-xl  " />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-32 rounded-md shadow-lg border bg-white"
                    >
                      <DropdownMenuItem
                        onClick={() => {
                          setIsUpdateOpen(true);
                          setSelectedItem(item);
                        }}
                        className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                      >
                        <div className="flex gap-2">
                          <SquarePen size={16} /> Edit
                        </div>
                      </DropdownMenuItem>
                      {(role.isSuperAdmin || role.isOrgAdmin) && (
                        <DropdownMenuItem
                          onClick={() => {
                            setIsDeleteOpen(true);
                            setSelectedItem(item);
                          }}
                          className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                        >
                          <div className="flex gap-2">
                            <Trash2 size={16} /> Delete
                          </div>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {selectedItem && (
          <DeleteModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            item={selectedItem}
            onConfirm={(item) => {
              handleDeleteItem(item.id);
              setIsDeleteOpen(false);
              setSelectedItem(null);
            }}
            itemLabel="item"
          />
        )}
      </div>
    </div>
  );
}
